import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, 
  Phone, 
  LogOut, 
  Calendar, 
  Package, 
  Wallet, 
  CheckCircle2, 
  Loader2, 
  X 
} from 'lucide-react';
import { api } from "../services/api";

const PRIMARY = "#363C90";
const STATIC_AVATAR = "https://sell30.mydemowebs.in/images/avatar.png";

const Profile: React.FC = () => {
  const navigate = useNavigate();
  
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState("");

  const fetchProfile = async () => {
    try {
      const data = await api.get("/user");
      setUser(data);
      setName(data.name);
      setMobile(data.mobile);
    } catch (error: any) {
      console.error("Profile Fetch Error:", error.message);
      if (error.message.includes("401")) navigate('/'); 
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setUpdating(true);
    try {
      if (mobile !== user?.mobile) {
        await api.post("/profile/update-request", { name, mobile });
        setShowOtpModal(true);
      } else {
        await api.post("/profile/update", { name });
        alert("Profile updated successfully!");
        fetchProfile();
      }
    } catch (error: any) {
      alert(error.message);
    } finally {
      setUpdating(false);
    }
  };

  const verifyOtp = async () => {
    try {
      await api.post("/profile/verify-mobile", { mobile, otp });
      setShowOtpModal(false);
      setOtp("");
      alert("Mobile number updated!");
      fetchProfile();
    } catch (error: any) {
      alert(error.message);
    }
  };

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      localStorage.removeItem("user_token");
      window.location.href = "/";
    }
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#363C90]" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-12 px-6">
      <div className="max-w-[900px] mx-auto">
        <h1 className="text-3xl font-black text-slate-900 mb-10">Account Settings</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Left Column: Avatar & Quick Info */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-[32px] p-8 text-center border border-slate-100 shadow-sm">
              <div className="relative inline-block">
                <img 
                  src={STATIC_AVATAR} 
                  alt="Avatar" 
                  className="w-24 h-24 rounded-full border-4 border-white shadow-lg mx-auto"
                />
                <div className="absolute bottom-1 right-1 bg-green-500 w-5 h-5 rounded-full border-4 border-white" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mt-4">{user?.name}</h2>
              <p className="text-sm text-slate-500 flex items-center justify-center gap-1.5 mt-1 font-medium">
                <Calendar size={14} /> Member since {user?.member_since || '2024'}
              </p>
            </div>

            {/* Quick Stats Cards */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[#363C90] p-6 rounded-[24px] text-white shadow-lg shadow-indigo-100">
                <Package size={20} className="mb-2 opacity-60" />
                <p className="text-2xl font-black">{user?.orders_count || 0}</p>
                <p className="text-[11px] font-bold uppercase tracking-wider opacity-70">Orders</p>
              </div>
              <div className="bg-white p-6 rounded-[24px] text-[#363C90] border border-slate-100 shadow-sm">
                <Wallet size={20} className="mb-2 text-indigo-200" />
                <p className="text-2xl font-black text-slate-900">₹{user?.total_earned || 0}</p>
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Earned</p>
              </div>
            </div>

            <button 
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-3 py-4 text-red-500 font-bold text-sm bg-red-50 hover:bg-red-100 rounded-2xl transition-all"
            >
              <LogOut size={18} /> Sign Out
            </button>
          </div>

          {/* Right Column: Update Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-[32px] p-8 md:p-10 border border-slate-100 shadow-sm">
              <h3 className="text-lg font-black text-slate-900 mb-8">Personal Information</h3>
              
              <form onSubmit={handleUpdate} className="space-y-6">
                <div>
                  <label className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2 block ml-1">Full Name</label>
                  <div className="relative group">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-[#363C90] transition-colors" size={20} />
                    <input 
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full pl-12 pr-6 py-4 bg-slate-50 border-2 border-transparent focus:border-[#363C90]/20 focus:bg-white rounded-2xl text-slate-800 font-bold outline-none transition-all"
                      placeholder="Enter your name"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2 block ml-1">Mobile Number</label>
                  <div className="relative group">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-[#363C90] transition-colors" size={20} />
                    <input 
                      type="tel"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value)}
                      className="w-full pl-12 pr-6 py-4 bg-slate-50 border-2 border-transparent focus:border-[#363C90]/20 focus:bg-white rounded-2xl text-slate-800 font-bold outline-none transition-all"
                      placeholder="Mobile number"
                    />
                  </div>
                </div>

                <button 
                  type="submit"
                  disabled={updating}
                  className="w-full py-5 bg-[#363C90] text-white rounded-[20px] font-black text-lg hover:bg-[#2a2f70] transition-all shadow-xl shadow-indigo-100 flex items-center justify-center gap-3 disabled:opacity-70"
                >
                  {updating ? <Loader2 className="animate-spin" /> : 'Update Profile'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* OTP Modal Overlay */}
      {showOtpModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 backdrop-blur-md bg-slate-900/40">
          <div className="bg-white w-full max-w-md rounded-[40px] p-10 shadow-2xl text-center">
            <div className="w-20 h-20 bg-indigo-50 text-[#363C90] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 size={40} />
            </div>
            <h2 className="text-2xl font-black text-slate-900 mb-2">Verify Mobile</h2>
            <p className="text-slate-500 text-sm font-medium mb-8">
              Please enter the 4-digit code sent to <br /> <span className="text-slate-900 font-bold">{mobile}</span>
            </p>
            
            <input 
              type="text"
              maxLength={4}
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="0000"
              className="w-32 text-center text-3xl font-black tracking-[10px] pb-2 border-b-4 border-[#363C90] outline-none mb-10 text-slate-900"
            />

            <div className="flex flex-col gap-3">
              <button 
                onClick={verifyOtp}
                className="w-full py-4 bg-[#363C90] text-white rounded-2xl font-black"
              >
                Verify & Save
              </button>
              <button 
                onClick={() => setShowOtpModal(false)}
                className="text-slate-400 font-bold text-sm flex items-center justify-center gap-2"
              >
                <X size={16} /> Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;